/**
 * OIDC PKCE provider for the auth seam (ADR-012 web path; ADR-081 step 4,
 * contract-gaps item G — closed by devops/SRE 2026-07-10).
 *
 * Zitadel per-surface SPA app: authorization_code + PKCE, PUBLIC client (no
 * secret), refresh grant enabled. The shell injects its surface's config
 * (issuer / clientId / projectId) — web-ui first; desktop and admin-tenant get
 * their OWN Zitadel apps (per-surface rule, ADR-072/081 Q2) but reuse this
 * provider with their config.
 *
 * Contract pins (devops inbox 2026-07-10T02-35 + SRE clientId hand-off):
 *  - The callback path is EXACTLY `/auth/callback` — Zitadel redirect matching
 *    is exact-match and the bootstrap script is create-only; changing it is a
 *    devops reconcile-pass ask, not a client-side tweak.
 *  - The project-id aud scope is LOAD-BEARING: without it the access token's
 *    `aud` lacks the project id and the ingress forwardAuth validator
 *    (INGRESS_AUTH_AUDIENCE) rejects every request.
 *  - The post-logout redirect URI is each surface's own origin ROOT (exact-match,
 *    trailing slash — devops infra!684, 2026-07-22): logout is RP-initiated
 *    (signoutRedirect → Zitadel end_session_endpoint → back to the origin root),
 *    so the IdP session is actually ended, not just the local tokens dropped.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { UserManager, WebStorageStateStore } from 'oidc-client-ts';
import { detectLocale } from '../i18n';
/** Pinned with devops (2026-07-10): exact-match in Zitadel, same path per surface. */
export const OIDC_CALLBACK_PATH = '/auth/callback';
export function buildOidcScope(projectId) {
    // offline_access requests the refresh token (grant is enabled on the app);
    // profile+email feed Auth.user for chrome display.
    return `openid profile email offline_access urn:zitadel:iam:org:project:id:${projectId}:aud`;
}
/** Pure settings builder — separated from window access for testability. */
export function buildOidcSettings(cfg, origin) {
    return {
        authority: cfg.issuer,
        client_id: cfg.clientId,
        redirect_uri: origin + OIDC_CALLBACK_PATH,
        // RP-initiated logout lands back on the surface's origin root — the exact-match
        // URI devops registered per surface (infra!684). Zitadel rejects an unregistered
        // post-logout redirect, so this MUST stay the registered origin root ('/').
        post_logout_redirect_uri: origin + '/',
        response_type: 'code',
        scope: buildOidcScope(cfg.projectId),
        automaticSilentRenew: true,
    };
}
/**
 * Logout drops a parked e-mail step-up edit. The key is repeated HERE on purpose: importing
 * `account/profileDraft` would put that module in the shell chunk (it rides every route's bytes; web!828
 * lighthouse), while only the lazy `/account` chunk needs it. `test/oidcAuthStability.test.ts` pins this
 * string to `PROFILE_DRAFT_KEY`, so the two cannot drift apart silently.
 */
function clearParkedProfileDraft() {
    try {
        window.sessionStorage.removeItem('agnivo.account.profileDraft');
    }
    catch {
        /* nothing to clear */
    }
}
function createUserManager(cfg) {
    return new UserManager({
        ...buildOidcSettings(cfg, window.location.origin),
        // session-scoped token store: nothing outlives the tab; a fresh tab
        // re-authenticates (usually silently via the IdP cookie session)
        userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    });
}
/**
 * Login state round-trips the pre-login location through the IdP redirect.
 * Only same-origin PATHS survive the trip — anything absolute/protocol-relative
 * collapses to '/' (open-redirect guard).
 */
export function sanitizeReturnTo(returnTo) {
    if (typeof returnTo !== 'string')
        return '/';
    // '/\' is rejected too: browsers normalize backslash to slash, so
    // '/\evil.example' navigates protocol-relative just like '//evil.example'
    return returnTo.startsWith('/') && !returnTo.startsWith('//') && !returnTo.startsWith('/\\')
        ? returnTo
        : '/';
}
export function toAuthUser(user) {
    const profile = user.profile;
    return {
        name: profile.name ?? profile.preferred_username ?? profile.email ?? '',
        email: profile.email,
        // Live-verified 2026-09-18 against the ACTIVE hosted Login-v1 policy (initiative
        // 019 job 1): registration is gated on an "Activate User" email-code step before
        // the account can be used, so Zitadel DOES enforce verification at signup. This
        // claim is the client's only way to know that outcome — read it, don't infer it
        // from mere presence of `email`.
        emailVerified: profile.email_verified,
        sub: profile.sub,
    };
}
/**
 * The reactive OIDC `Auth` for the seam — same hook shape as the mini-app's
 * initData provider. Initial state resolves from the session store; transitions
 * follow the manager's events (loaded / unloaded / expired).
 */
export function useOidcAuth(cfg) {
    // keyed on the primitive fields so an inline config object literal doesn't
    // recreate the manager every render
    const { issuer, clientId, projectId, logoutVia } = cfg;
    const manager = useMemo(() => createUserManager({ issuer, clientId, projectId }), [issuer, clientId, projectId]);
    const [state, setState] = useState({
        status: 'loading',
        user: null,
    });
    useEffect(() => {
        let disposed = false;
        const apply = (user) => {
            if (disposed)
                return;
            if (user && !user.expired)
                setState({ status: 'authenticated', user: toAuthUser(user) });
            else
                setState({ status: 'unauthenticated', user: null });
        };
        manager.getUser().then(apply, () => apply(null));
        const onLoaded = (user) => apply(user);
        const onCleared = () => apply(null);
        manager.events.addUserLoaded(onLoaded);
        manager.events.addUserUnloaded(onCleared);
        // silent renew failing means the session will lapse at token expiry;
        // expiry itself is the state flip (renew errors alone stay authenticated
        // while the current access token is still valid)
        manager.events.addAccessTokenExpired(onCleared);
        return () => {
            disposed = true;
            manager.events.removeUserLoaded(onLoaded);
            manager.events.removeUserUnloaded(onCleared);
            manager.events.removeAccessTokenExpired(onCleared);
        };
    }, [manager]);
    /**
     * ⛔ PINNED TO `manager`, NOT to `state` — and this is load-bearing for a P1 data-loss defect,
     * not a render-count optimisation. Do not fold it back into the memo below.
     *
     * `oidc-client-ts` fires `UserLoaded` on **silent renew**: on a timer, during any normal session,
     * with no user action. The handler above calls `setState({…})` with a fresh object literal, so
     * `state` identity changes even when the user is byte-identical. When `getAccessToken` lived in
     * the `[manager, state]` memo, that renew gave every consumer a new callback → every
     * `useMemo([auth.getAccessToken, …])` api client was rebuilt → every load effect on `[api, …]`
     * re-ran → screens that re-seed EDITABLE state from the response discarded whatever the operator
     * had typed. `admin-tenant/AgentDetailScreen` (form + `formRef`, the autosave writer's own source
     * of truth) and `admin-platform/PlanDetailScreen` (three drafts) both do exactly that. The
     * operator sees a field snap back mid-sentence, with no error and nothing to attribute it to.
     *
     * Architect's ruling, 2026-08-16T14:49:36Z — the defect has ONE owner, so it gets ONE site.
     * Pinning `api` behind a ref in the nine consuming screens was explicitly rejected. The house
     * rule: **per-request credentials are CALL-TIME reads, never render-identity inputs.**
     *
     * Identity alone is not the goal — a `[]`-pinned closure over a stale token would satisfy it and
     * 401 instead. The token is read from `manager` at call time, so this stays correct across a
     * rotation AND returns null after sign-out. All four properties are pinned in
     * `test/oidcAuthStability.test.ts`; the two identity cases red against the pre-fix code.
     */
    const getAccessToken = useCallback(async () => {
        const user = await manager.getUser();
        return user && !user.expired ? user.access_token : null;
    }, [manager]);
    // Same call-time rule as `getAccessToken` (and pinned to `manager` for the same reason): a silent renew
    // must not hand every consumer a new callback.
    const getIdToken = useCallback(async () => {
        const user = await manager.getUser();
        return user && !user.expired && user.id_token ? user.id_token : null;
    }, [manager]);
    const reauthenticate = useCallback(({ maxAgeSeconds }) => {
        void manager.signinRedirect({
            state: { returnTo: window.location.pathname + window.location.search },
            ui_locales: detectLocale(),
            max_age: maxAgeSeconds,
        });
    }, [manager]);
    return useMemo(() => ({
        status: state.status,
        user: state.user,
        getAccessToken,
        getIdToken,
        reauthenticate,
        login: () => {
            void manager.signinRedirect({
                state: { returnTo: window.location.pathname + window.location.search },
                // Personalization (Dmitry DM 314): pass the app's OWN resolved locale as
                // ui_locales so the Zitadel login page renders in the SAME language as the
                // app the user came from, instead of Zitadel guessing from Accept-Language.
                ui_locales: detectLocale(),
            });
        },
        // RP-initiated sign-out (post-logout URI registered per surface, infra!684):
        // signoutRedirect drops the local tokens AND redirects through Zitadel's
        // end_session_endpoint so the IdP session is actually ended — then lands back
        // on the origin root. Fallback: if the redirect can't be built (no
        // end_session_endpoint in discovery / navigator failure), still clear local
        // tokens so a failed signout never leaves a half-authenticated tab, then
        // land on the gate.
        logout: () => {
            clearParkedProfileDraft();
            const plain = () => manager
                .signoutRedirect({
                // Same personalization on the logout page (Dmitry DM 314): render it in
                // the app's language. signoutRedirect doesn't type `ui_locales` (only
                // signinRedirect does), so pass it through extraQueryParams — Zitadel's
                // end_session_endpoint reads the standard `ui_locales` query param.
                extraQueryParams: { ui_locales: detectLocale() },
            })
                .catch(() => {
                void manager.removeUser().finally(() => window.location.assign('/'));
            });
            void (logoutVia ? logoutVia(manager).catch(plain) : plain());
        },
    }), [manager, state, getAccessToken, getIdToken, reauthenticate, logoutVia]);
}
/**
 * Completes the redirect leg on the callback route: exchanges the code (PKCE
 * verifier comes from the state store the login leg wrote) and returns the
 * sanitized same-origin path to land on. The caller should hard-navigate
 * (location.replace) so the app remounts and picks the user up from the store.
 */
export async function completeOidcCallback(cfg) {
    const manager = createUserManager(cfg);
    const user = await manager.signinRedirectCallback();
    return sanitizeReturnTo(user.state?.returnTo);
}
//# sourceMappingURL=oidcAuth.js.map